import React, { useId, useRef, useState } from "react";

import { PatientDobFilterFields, PatientDobFilterToggle } from "./patient_dob_filter";
import { patientMeta, patientSearchSummary, usePatientSearch } from "./patient_search";
import type { Patient } from "./patient_search";

interface PatientPickerProps {
  searchUrl: string;
  inputName: string;
  inputId?: string;
  label?: string;
  required?: boolean;
  emptySelectionLabel?: string;
  collapseSearchOnSelect?: boolean;
  selectedPatient?: Patient | null;
  reloadOnSelect?: boolean;
}

function PatientCard({
  patient,
  children,
}: {
  patient: Patient;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-neutral-200 bg-neutral-50/80 p-3 dark:border-neutral-800 dark:bg-neutral-950/60">
      <div className="flex flex-wrap items-start justify-between gap-2.5">
        <div>
          <p className="text-sm font-semibold text-neutral-950 dark:text-white">{patient.fullName}</p>
          {patientMeta(patient).length > 0 ? (
            <p className="mt-0.5 text-sm text-neutral-600 dark:text-neutral-300">{patientMeta(patient).join(" | ")}</p>
          ) : null}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

export default function PatientPicker({
  searchUrl,
  inputName,
  inputId,
  label = "Patient",
  required = false,
  emptySelectionLabel = "No patient linked",
  collapseSearchOnSelect = false,
  selectedPatient: initialSelectedPatient = null,
  reloadOnSelect = false,
}: PatientPickerProps) {
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(initialSelectedPatient);
  const [searchVisible, setSearchVisible] = useState(!collapseSearchOnSelect || !initialSelectedPatient);
  const [dobFiltersVisible, setDobFiltersVisible] = useState(false);
  const hiddenInputRef = useRef<HTMLInputElement | null>(null);
  const searchInputId = useId();
  const dobFiltersId = useId();
  const {
    clearSearch,
    dobFilters,
    errorMessage,
    hasDobFilters,
    loading,
    query,
    results,
    setDobFilters,
    setQuery,
    trimmedQuery,
  } = usePatientSearch(searchUrl);

  const resultSummary = patientSearchSummary({
    trimmedQuery,
    hasDobFilters,
    loading,
    errorMessage,
    resultsCount: results.length,
  });

  const reloadForm = (patient: Patient | null) => {
    if (!reloadOnSelect) return;

    const input = hiddenInputRef.current;
    const form = input?.closest("form");
    if (!input || !form) return;

    const url = new URL(window.location.href);
    url.search = "";

    const formData = new FormData(form);
    if (patient) {
      formData.set(inputName, String(patient.id));
    } else {
      formData.delete(inputName);
    }

    formData.delete("authenticity_token");
    formData.delete("_method");
    formData.delete("commit");

    const params = new URLSearchParams();

    formData.forEach((value, key) => {
      if (value instanceof File) return;

      params.append(key, String(value));
    });

    url.search = params.toString();
    window.location.assign(url.toString());
  };

  const selectPatient = (patient: Patient) => {
    setSelectedPatient(patient);
    clearSearch();
    setSearchVisible(false);
    reloadForm(patient);
  };

  const clearPatient = () => {
    setSelectedPatient(null);
    clearSearch();
    setSearchVisible(true);
    reloadForm(null);
  };

  const beginChangingPatient = () => {
    clearSearch();
    setSearchVisible(true);
  };

  const cancelChangingPatient = () => {
    clearSearch();
    setSearchVisible(false);
  };

  const showSelectedPatient = Boolean(selectedPatient) && (!collapseSearchOnSelect || !searchVisible);
  const showSearch = !collapseSearchOnSelect || !selectedPatient || searchVisible;

  return (
    <div className="space-y-3">
      <input
        ref={hiddenInputRef}
        id={inputId}
        name={inputName}
        type="hidden"
        value={selectedPatient?.id || ""}
        required={required}
      />

      {showSelectedPatient ? (
        <div>
          <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200">{label}</p>
          <div className="mt-2">
            <PatientCard patient={selectedPatient!}>
              <a href={selectedPatient!.patientPath} className="app-button-secondary" target="_blank" rel="noreferrer">
                Open chart
              </a>
              {collapseSearchOnSelect ? (
                <button type="button" className="app-button-secondary" onClick={beginChangingPatient}>
                  Change patient
                </button>
              ) : null}
              {!required ? (
                <button type="button" className="app-button-secondary" onClick={clearPatient}>
                  Clear
                </button>
              ) : null}
            </PatientCard>
          </div>
        </div>
      ) : null}

      {showSearch ? (
        <div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-0.5">
              <label htmlFor={searchInputId} className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                Find patient
              </label>
              <PatientDobFilterToggle
                controlsId={dobFiltersId}
                expanded={dobFiltersVisible}
                filters={dobFilters}
                onToggle={() => setDobFiltersVisible((visible) => !visible)}
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {collapseSearchOnSelect && selectedPatient ? (
                <button type="button" className="app-button-secondary" onClick={cancelChangingPatient}>
                  Keep current patient
                </button>
              ) : null}
            </div>
          </div>
          {!selectedPatient && collapseSearchOnSelect ? (
            <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">{emptySelectionLabel}</p>
          ) : null}
          <input
            id={searchInputId}
            type="search"
            value={query}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setQuery(event.target.value)}
            placeholder="Search name, phone, or email"
            autoComplete="off"
            className="app-input mt-2"
          />
          {dobFiltersVisible ? <PatientDobFilterFields filters={dobFilters} id={dobFiltersId} onChange={setDobFilters} /> : null}
          <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">{resultSummary}</p>
        </div>
      ) : !selectedPatient ? (
        <div>
          <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200">{label}</p>
          <div className="mt-2 rounded-md border border-dashed border-neutral-300 bg-white px-3 py-2.5 text-sm text-neutral-500 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-400">
            {emptySelectionLabel}
          </div>
        </div>
      ) : null}

      {results.length > 0 ? (
        <div className="space-y-2.5">
          {results.map((patient) => {
            const selected = String(selectedPatient?.id) === String(patient.id);

            return (
              <PatientCard key={patient.id} patient={patient}>
                <a href={patient.patientPath} className="app-button-secondary" target="_blank" rel="noreferrer">
                  Open chart
                </a>
                <button
                  type="button"
                  className={selected ? "app-button-secondary" : "app-button-primary"}
                  onClick={() => selectPatient(patient)}
                  disabled={selected}
                >
                  {selected ? "Selected" : "Select patient"}
                </button>
              </PatientCard>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
